import React, { useState, useEffect } from 'react';
import { track } from '@lf/analytics-events';
import { LoadingButton } from '@lf/ui-primitives';
import { getParentLinkStatus, createParentLinkRequest, cancelParentLinkRequest } from '../../api/lfApiChildLink';

function ParentLinkSection() {
    const [linkStatus, setLinkStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [parentEmail, setParentEmail] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    async function fetchStatus() {
        const response = await getParentLinkStatus(() => {});
        if (response && response.data) {
            setLinkStatus(response.data);
        }
        setIsLoading(false);
    }

    useEffect(() => {
        fetchStatus();
    }, []);

    async function handleSendRequest(e) {
        if (e) e.preventDefault();
        setError('');
        setSuccessMessage('');

        if (!parentEmail) {
            setError('Please enter an email address.');
            return;
        }

        const response = await createParentLinkRequest(parentEmail, () => {});
        if (response && response.data && response.data.success) {
            track('parent_link_requested', {
                child_id: response.data.child_id || response.data.user_profile_id || '',
            });
            setSuccessMessage('Link request sent!');
            setShowForm(false);
            setParentEmail('');
            await fetchStatus();
        } else {
            setError(response?.data?.error || 'Failed to send request.');
            track('form_submission_failed', {
                form: 'parent_link_request',
                field: 'parent_email',
                reason: response?.data?.error || 'unknown',
            });
        }
    }

    async function handleCancel() {
        setError('');
        const response = await cancelParentLinkRequest(() => {});
        if (response && response.data && response.data.success) {
            await fetchStatus();
        } else {
            setError(response?.data?.error || 'Failed to cancel request.');
        }
    }

    if (isLoading || !linkStatus) return null;

    const { is_linked, parent_link, attempts_remaining } = linkStatus;

    return (
        <div className="mt-6">
            <h2 className="text-lg font-semibold mb-2">Parent/Guardian</h2>

            {is_linked && parent_link && (
                <div className="text-sm text-gray-600">
                    {parent_link.parent_email} (Verified)
                </div>
            )}

            {!is_linked && parent_link && parent_link.status === 'pending' && (
                <div className="text-sm">
                    <div className="text-gray-600 mb-1">
                        {parent_link.parent_email} (Pending)
                    </div>
                    <div className="text-gray-400 text-xs mb-2">
                        Sent: {new Date(parent_link.inserted_at).toLocaleDateString()}
                    </div>
                    <button
                        className="bg-gray-300 text-gray-700 py-1 px-3 rounded text-sm"
                        onClick={handleCancel}>
                        Cancel Request
                    </button>
                </div>
            )}

            {!is_linked && (!parent_link || parent_link.status !== 'pending') && attempts_remaining > 0 && (
                <div className="text-sm">
                    <div className="text-gray-500 mb-2">Not linked</div>
                    {!showForm ? (
                        <button
                            className="bg-blue-500 text-white py-1 px-3 rounded text-sm"
                            onClick={() => setShowForm(true)}>
                            Link a parent account
                        </button>
                    ) : (
                        <form onSubmit={handleSendRequest} className="flex flex-col gap-2 max-w-xs">
                            <input
                                type="email"
                                className="border border-gray-300 p-1 text-sm"
                                placeholder="Parent/Guardian Email"
                                value={parentEmail}
                                onChange={(e) => setParentEmail(e.target.value)}
                            />
                            <div className="flex gap-2">
                                <LoadingButton
                                    text="Send Link Request"
                                    onClick={handleSendRequest}
                                />
                                <button
                                    type="button"
                                    className="text-gray-500 text-sm"
                                    onClick={() => { setShowForm(false); setError(''); }}>
                                    Cancel
                                </button>
                            </div>
                            <div className="text-xs text-gray-400">
                                You have {attempts_remaining} attempt{attempts_remaining !== 1 ? 's' : ''} remaining.
                            </div>
                        </form>
                    )}
                </div>
            )}

            {!is_linked && (!parent_link || parent_link.status !== 'pending') && attempts_remaining <= 0 && (
                <div className="text-sm">
                    <div className="text-gray-500 mb-1">Not linked</div>
                    <div className="text-gray-400">
                        You have used all 3 link attempts. Contact support for help.
                    </div>
                </div>
            )}

            {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
            {successMessage && <div className="text-green-600 text-sm mt-2">{successMessage}</div>}
        </div>
    );
}

export default ParentLinkSection;
